import { useRef, useState, useEffect, useLayoutEffect, forwardRef, useImperativeHandle } from "react";

// CommandInputHandle (via ref): { fill: (syntax: string) => void }
const CommandInput = forwardRef(function CommandInput({
  prompt = "analyst@lakeview:~$",
  placeholder = "type a command…",
  onSubmit,
  history = [],
  candidates = [],
  disabled = false,
}, ref) {
  const inputRef = useRef(null);
  const mirrorRef = useRef(null);
  const filledPrefixRef = useRef("");

  const [value, setValue] = useState("");
  const [argGhost, setArgGhost] = useState("");
  const [ghostLeft, setGhostLeft] = useState(0);
  const [histIdx, setHistIdx] = useState(-1);
  const [acList, setAcList] = useState([]);
  const [acIdx, setAcIdx] = useState(0);
  const [acQuery, setAcQuery] = useState("");

  const acOpen = acList.length > 0;

  useLayoutEffect(() => {
    if (mirrorRef.current) setGhostLeft(mirrorRef.current.scrollWidth);
  }, [value]);

  useImperativeHandle(ref, () => ({
    fill: (syntax) => {
      const spaceIdx = syntax.indexOf(" ");
      if (spaceIdx !== -1 && syntax.slice(spaceIdx + 1).includes("<")) {
        const prefix = syntax.slice(0, spaceIdx + 1);
        const ghost = syntax.slice(spaceIdx + 1);
        setValue(prefix);
        setArgGhost(ghost);
        filledPrefixRef.current = prefix;
      } else {
        setValue(syntax);
        setArgGhost("");
        filledPrefixRef.current = "";
      }
      setAcList([]);
      requestAnimationFrame(() => inputRef.current?.focus());
    },
  }));

  useEffect(() => { if (!disabled) inputRef.current?.focus(); }, [disabled]);

  function clearGhostIfDiverged(newVal) {
    if (argGhost && newVal !== filledPrefixRef.current) {
      setArgGhost("");
      filledPrefixRef.current = "";
    }
  }

  function openAc(prefix, matches) {
    setAcQuery(prefix);
    setAcList(matches);
    setAcIdx(0);
    setValue(matches[0] + " ");
  }

  function handleKeyDown(e) {
    if (disabled) return;
    if (e.key === "Tab") {
      e.preventDefault();
      if (!acOpen) {
        const prefix = value.trim();
        if (prefix.includes(" ")) return;
        const matches = candidates.filter((c) => c.startsWith(prefix) && c !== prefix);
        if (matches.length === 0) return;
        if (matches.length === 1) {
          setValue(matches[0] + " ");
        } else {
          openAc(prefix, matches);
        }
      } else {
        const next = (acIdx + 1) % acList.length;
        setAcIdx(next);
        setValue(acList[next] + " ");
      }
    } else if (e.key === "Escape") {
      if (acOpen) { setAcList([]); setValue(acQuery); }
    } else if (e.key === "Enter") {
      const cmd = value.trim();
      if (cmd) {
        onSubmit(cmd);
        setValue("");
        setArgGhost("");
        filledPrefixRef.current = "";
        setHistIdx(-1);
        setAcList([]);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (acOpen) return;
      const next = Math.min(histIdx + 1, history.length - 1);
      setHistIdx(next);
      setValue(history[next] ?? "");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (acOpen) return;
      const next = Math.max(histIdx - 1, -1);
      setHistIdx(next);
      setValue(next === -1 ? "" : (history[next] ?? ""));
    } else {
      if (acOpen) setAcList([]);
    }
  }

  return (
    <div style={{ borderTop: "1px solid #334155", paddingTop: "6px", position: "relative" }}>
      {acOpen && (
        <div style={{
          position: "absolute", bottom: "100%", left: 0, right: 0,
          background: "#0f172a", border: "1px solid #334155", borderRadius: "4px",
          marginBottom: "4px", zIndex: 10, overflow: "hidden",
        }}>
          {acList.map((c, i) => (
            <div
              key={c}
              style={{
                padding: "4px 10px",
                fontFamily: "monospace",
                fontSize: "13px",
                color: i === acIdx ? "#34d399" : "#94a3b8",
                background: i === acIdx ? "#1e293b" : "transparent",
                cursor: "pointer",
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                setValue(c + " ");
                setAcList([]);
                inputRef.current?.focus();
              }}
            >
              {c}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ color: "#34d399", fontFamily: "monospace", fontSize: "13px", whiteSpace: "nowrap", flexShrink: 0 }}>
          {prompt}
        </span>
        <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", minWidth: 0 }}>
          <span
            ref={mirrorRef}
            aria-hidden
            style={{
              position: "absolute", visibility: "hidden", whiteSpace: "pre",
              fontFamily: "monospace", fontSize: "13px", pointerEvents: "none",
            }}
          >
            {value}
          </span>
          <input
            ref={inputRef}
            value={value}
            disabled={disabled}
            onChange={(e) => {
              const newVal = e.target.value;
              setValue(newVal);
              clearGhostIfDiverged(newVal);
              if (acOpen) setAcList([]);
            }}
            onKeyDown={handleKeyDown}
            placeholder={argGhost ? "" : placeholder}
            autoComplete="off"
            spellCheck={false}
            style={{
              width: "100%", background: "transparent", border: "none", outline: "none",
              color: "#e2e8f0", fontFamily: "monospace", fontSize: "13px",
              padding: 0, opacity: disabled ? 0.5 : 1,
            }}
          />
          {argGhost && (
            <span
              aria-hidden
              style={{
                position: "absolute", left: ghostLeft, color: "#475569",
                fontFamily: "monospace", fontSize: "13px",
                whiteSpace: "pre", pointerEvents: "none", userSelect: "none",
              }}
            >
              {argGhost}
            </span>
          )}
        </div>
        <span style={{ color: "#475569", fontSize: "11px", whiteSpace: "nowrap", flexShrink: 0 }}>
          <kbd style={{ padding: "1px 4px", borderRadius: "3px", border: "1px solid #475569", fontSize: "10px" }}>Tab</kbd>
          {" "}complete·{" "}
          <kbd style={{ padding: "1px 4px", borderRadius: "3px", border: "1px solid #475569", fontSize: "10px" }}>↑</kbd>
          {" "}history
        </span>
      </div>
    </div>
  );
});

export default CommandInput;
